import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import TrackerResumeLine from './TrackerResumeLine';
import TrackerResumeTitle from './TrackerResumeTitle';

import EndPointService from "../services/endpoint";
import { REGISTERED_PORTS, REGISTERED_SERVERS } from '../services/constants';
import { ActionStatus, BullkActionStatus, Tracker } from '../types';
import Server from './Server';

type BulkRedirectionProps = {
    trackers: Tracker[];
};

export default function TrackersBulkRedirection({trackers}: BulkRedirectionProps) {
    const [saving, setSaving] = useState(false);
    const [sendingFleet, setSendingFleet] = useState(false);
    const [fleetStatus, setFleetStatus] = useState<BullkActionStatus>({});
    const [server, setServer] = useState('');
    const [port, setPort] = useState(-1);
    const [errorsOnServer, setErrorsOnServer] = useState<string[]>([])
    const [errorsOnPort, setErrorsOnPort] = useState<string[]>([])
    const [warnsOnServer, setWarnsOnServer] = useState<string[]>([])
    const [warnsOnPort, setWarnsOnPort] = useState<string[]>([])

    const [status, setStatus] = useState<BullkActionStatus>({})

    const navigate = useNavigate();

    useEffect(() => {
        if (trackers.length > 0) {
            setServer(trackers[0].server_url);
            setPort(trackers[0].server_port);
        }
        setAllStatus(trackers, ActionStatus.TO_ACT );
    }, [trackers])

    useEffect(() => {
        const errors: string[] = [];
        if (!server.match(/^[0-9a-zA-Z.]+$/)) {
            errors.push('Caractères autorisés: 0-9 a-z A-Z "."')
        }
        const regExpIP = /^[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}$/
        const regExpURL = /^[a-z][0-9a-z]*(\.[a-z][0-9a-z]*)+$/
        if (server.match(regExpIP) === null && server.match(regExpURL) === null) {
            errors.push('Format autorisé: IP (xxx.xxx.xxx.xxx) ou URL (xxx.mclloyd.com)')
        }
        if (server.length > 15) {
            errors.push('Longueur maximum: 15 carcatères')
        }
        setErrorsOnServer(errors);
        const warns: string[] = [];
        if (!REGISTERED_SERVERS.includes(server)) {
            warns.push(`Êtes vous sûr du nom de ce serveur ?`)
        }
        setWarnsOnServer(warns);
    }, [server])

    useEffect(() => {
        const errors: string[] = [];
        if (typeof port !== 'number') {
            errors.push('Doit être un nombre')
        }
        if (port%1>0) {
            errors.push('Doit être un nombre entier')
        }
        if (port<0) {
            errors.push('Doit être un nombre positif')
        }
        setErrorsOnPort(errors);
        const warns: string[] = [];
        if (!REGISTERED_PORTS.includes(port)) {
            warns.push(`Êtes vous sûr du port de ce serveur ?`)
        }
        setWarnsOnPort(warns);
    }, [port])

    function setAllStatus(trackers: Tracker[], statusToSet: ActionStatus) {
        const status: BullkActionStatus = {};
        trackers.forEach(tracker => {
            status[tracker.id] = statusToSet;
        });
        setStatus(status);
    }

    async function sendAllToFleet() {
        setSendingFleet(true);
        const initial: BullkActionStatus = {};
        for (const t of trackers) initial[t.id] = ActionStatus.TO_ACT;
        setFleetStatus({ ...initial });

        for (const tracker of trackers) {
            const els = tracker.name.split('_');
            if (els.length !== 3 || els[0] !== 'Rev' || els[1].length !== 1 || isNaN(Number(els[2]))) {
                setFleetStatus(prev => ({ ...prev, [tracker.id]: ActionStatus.ERROR }));
                continue;
            }
            setFleetStatus(prev => ({ ...prev, [tracker.id]: ActionStatus.ACTING }));
            try {
                await EndPointService.sendtofleet(tracker.hardware_id, els[2], els[0] + els[1], String(tracker.version));
                setFleetStatus(prev => ({ ...prev, [tracker.id]: ActionStatus.ACTED }));
            } catch {
                setFleetStatus(prev => ({ ...prev, [tracker.id]: ActionStatus.ERROR }));
            }
        }
        setSendingFleet(false);
    }

    function setFormPort(value: string) {
        setPort(Number(value));
    }

    function handleSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (errorsOnServer.length + errorsOnPort.length > 0 ) {
            alert('Il existe des erreurs. Corrigez les avant de valider.')
            return;
        }
        setSaving(true);
        setAllStatus(trackers, ActionStatus.ACTING);
        EndPointService
            .redirectAll(trackers, {url: server, port: port})
            .then(response => {
                setSaving(false);
                setAllStatus(trackers, ActionStatus.ACTED);
                setTimeout(() => {
                    navigate("/trackers", { replace: false });
                }, 1000)
            }).catch(() => {
                setSaving(false);
                setAllStatus(trackers, ActionStatus.ERROR);
            })
    }

    return (
        <div className="block-bulk">
            <table>
                <thead>
                    <TrackerResumeTitle />
                </thead>
                <tbody>
                    {
                        trackers.map(tracker => <TrackerResumeLine key={tracker.id} tracker={tracker} status={status[tracker.id]} fleetStatus={fleetStatus[tracker.id]} />)
                    }
                </tbody>
            </table>
            <div className="block-infos">
                Ces trackers vont être redirigés vers <span className="block-infos-target">{server}:{port}</span>
            </div>
            <form className="block-form" onSubmit={handleSubmit}>
                <div className={'line-input'
                        + ( errorsOnServer.length > 0 ? ' has-errors' : '')
                        + ( warnsOnServer.length > 0 ? ' has-warns' : '')}>
                    <Server server={server} setServer={setServer} setPort={setPort}/>
                    <span className="errors">
                        {
                            errorsOnServer.map((error, index) => <span className="error" key={index}>{error}</span>)
                        }
                    </span>
                    <span className="warns">
                        {
                            warnsOnServer.map((warn, index) => <span className="warn" key={index}>{warn}</span>)
                        }
                    </span>
                </div>
                <div className={'line-input'
                        + ( errorsOnPort.length > 0 ? ' has-errors' : '')
                        + ( warnsOnPort.length > 0 ? ' has-warns' : '')}>
                    <label htmlFor="port">Port:</label>
                    <input
                        id="port"
                        type="number"
                        value={port}
                        onChange={(e) => setFormPort(e.target.value)}
                    />
                    <span className="errors">
                        {
                            errorsOnPort.map((error, index) => <span className="error" key={index}>{error}</span>)
                        }
                    </span>
                    <span className="warns">
                        {
                            warnsOnPort.map((warn, index) => <span className="warn" key={index}>{warn}</span>)
                        }
                    </span>
                </div>
                <div className="line-buttons">
                    <button className="cancel-button" onClick={() => {navigate("/trackers", { replace: false });}}>
                        <span>Annuler</span>
                    </button>
                    <button className="valid-button" type="submit" disabled={saving || sendingFleet}>
                        <span>Rediriger</span>
                        { saving
                            ? <FontAwesomeIcon icon="spinner" spin />
                            : <FontAwesomeIcon icon="forward" />
                        }
                    </button>
                    <button className="valid-button" type="button" disabled={saving || sendingFleet} onClick={sendAllToFleet}>
                        <span>To Fleet</span>
                        { sendingFleet
                            ? <FontAwesomeIcon icon="spinner" spin />
                            : <FontAwesomeIcon icon="arrow-right" />
                        }
                    </button>
                </div>
            </form>
        </div>
    );
}

